/* Same-origin preview tabs intentionally retain their opener so Customize can return to the authenticated admin tab. */
/* eslint-disable react/jsx-no-target-blank */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { FaDesktop, FaDownload, FaEllipsisH, FaExternalLinkAlt, FaRegCopy, FaSearch, FaTimes, FaUpload } from "react-icons/fa";
import { readThemeListCache, writeThemeListCache } from "../../../themes/themeCache";
import "../../../assets/admin/css/theme-library.css";
import renownPreview from "../../../assets/admin/theme-previews/renown-showcase.webp";
import electronicsPreview from "../../../assets/admin/theme-previews/electronics-showcase.webp";

// Card text (tagline, badge, features) and colors come from the theme API,
// which reads the built-in theme files in ecommerce-laravel/resources/themes.
function enrichTheme(theme) {
  return {
    ...theme,
    tagline: theme.tagline || "",
    badge: theme.badge || "",
    previewImage: theme.preview_image || theme.preview_image_fallback || "",
    primary_color: theme.primary_color || "#111827",
    accent_color: theme.accent_color || "#2563eb",
    button_color: theme.button_color || theme.primary_color || "#111827",
    button_text_color: theme.button_text_color || "#fff",
    button_style: theme.button_style || "rounded",
    features: Array.isArray(theme.features) ? theme.features : [],
  };
}

// Built-in themes come back from the API with is_default set.
function canRemoveTheme(theme) {
  return !theme.is_published && !theme.is_default;
}

const BUILT_IN_PREVIEWS = {
  prestige: renownPreview,
  electronics: electronicsPreview,
};

const SUPPORTED_BUILT_IN_KEYS = new Set(Object.keys(BUILT_IN_PREVIEWS));

function pruneRetiredBuiltIns(themes) {
  if (!Array.isArray(themes)) return [];
  return themes.filter((theme) => !theme?.is_default || SUPPORTED_BUILT_IN_KEYS.has(theme.theme_key));
}

function previewForTheme(theme) {
  const key = String(theme.theme_key || "").toLowerCase();
  const builtInKey = Object.keys(BUILT_IN_PREVIEWS).find((name) => key.includes(name));
  const configured = theme.previewImage || theme.preview_image || theme.preview_image_fallback;

  // The two official themes always use their approved screenshots. Copies and
  // imported themes keep their own generated snapshots.
  if (SUPPORTED_BUILT_IN_KEYS.has(key)) {
    return BUILT_IN_PREVIEWS[key];
  }

  // Older built-in definitions pointed to preview JPGs that were not shipped.
  // Custom/imported themes can still provide and use their own preview image.
  if (builtInKey && (!configured || /^\/assets\/themes\//i.test(configured))) {
    return BUILT_IN_PREVIEWS[builtInKey];
  }

  return configured || BUILT_IN_PREVIEWS[builtInKey] || BUILT_IN_PREVIEWS.prestige;
}


function ThemeArtwork({ theme, large = false }) {
  const [imageSrc, setImageSrc] = useState(() => previewForTheme(theme));

  useEffect(() => {
    setImageSrc(previewForTheme(theme));
  }, [theme]);

  return (
    <div className={`theme-artwork ${large ? "is-large" : ""}`}>
      <img
        src={imageSrc}
        alt=""
        loading="eager"
        decoding="async"
        onError={() => {
          if (imageSrc !== BUILT_IN_PREVIEWS.prestige) {
            setImageSrc(BUILT_IN_PREVIEWS.prestige);
          }
        }}
      />
    </div>
  );
}

function DemoModal({ theme, onClose }) {
  const [loaded, setLoaded] = useState(false);
  const demoUrl = `/theme-demo/${encodeURIComponent(theme.theme_key)}`;

  useEffect(() => {
    const onKeyDown = (event) => event.key === "Escape" && onClose();
    document.body.classList.add("theme-modal-open");
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.classList.remove("theme-modal-open");
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  return (
    <div className="theme-demo-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section className="theme-demo-modal" role="dialog" aria-modal="true" aria-label={`${theme.name} demo`}>
        <header className="theme-demo-head">
          <div className="theme-demo-title"><button type="button" className="theme-icon-button" onClick={onClose} aria-label="Close demo"><FaTimes /></button><div><strong>{theme.name}</strong><span>Real rendered theme demo</span></div></div>
          <span className="theme-demo-curated">Live components</span>
          <div className="theme-demo-actions"><a className="theme-secondary-button" href={demoUrl} target="_blank" rel="opener">Open full demo <FaExternalLinkAlt /></a><Link className="theme-primary-button" to={`/admin/theme-editor?theme=${theme.theme_key}&content=demo`}>Customize</Link></div>
        </header>
        <div className="theme-demo-content">
          <div className="theme-demo-stage">
            <div className="theme-showcase-frame">
              <div className="theme-showcase-browser"><i /><i /><i /><span>{theme.name} — real theme demo</span></div>
              {!loaded && <div className="theme-demo-loading"><span />Rendering theme demo…</div>}
              <iframe title={`${theme.name} real theme demo`} src={`${demoUrl}?embedded=1`} onLoad={() => setLoaded(true)} />
            </div>
          </div>
          <aside className="theme-demo-info">
            <span className="theme-demo-badge">{theme.badge || "Online store"}</span><h2>{theme.name}</h2><p className="theme-demo-tagline">{theme.tagline}</p><p>{theme.description}</p>
            <h3>Highlights</h3><ul>{(theme.features || []).map((feature) => <li key={feature}>{feature}</li>)}</ul>
            <dl><div><dt>Version</dt><dd>{theme.version || "1.0.0"}</dd></div><div><dt>By</dt><dd>{theme.author || "GetGrowCart"}</dd></div><div><dt>Status</dt><dd>{theme.is_published ? "Live" : "Draft"}</dd></div></dl>
          </aside>
        </div>
      </section>
    </div>
  );
}

export default function ThemeLibrary() {
  const cachedThemes = pruneRetiredBuiltIns(readThemeListCache());
  const [themes, setThemes] = useState(cachedThemes || []);
  const [loading, setLoading] = useState(cachedThemes.length === 0);
  const [busyKey, setBusyKey] = useState("");
  const [importing, setImporting] = useState(false);
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all");
  const [demoTheme, setDemoTheme] = useState(null);
  const importRef = useRef(null);

  const loadThemes = () => {
    if (!readThemeListCache()) setLoading(true);
    axios.get("/api/admin/themes").then((res) => {
      if (res.data.status === 200 && Array.isArray(res.data.themes) && res.data.themes.length) {
        const nextThemes = pruneRetiredBuiltIns(res.data.themes);
        setThemes(nextThemes);
        writeThemeListCache(nextThemes);
      }
    }).catch(() => setThemes((current) => current)).finally(() => setLoading(false));
  };

  useEffect(() => { document.title = "Themes"; loadThemes(); }, []);

  const catalog = useMemo(() => themes.map(enrichTheme), [themes]);
  const liveTheme = catalog.find((theme) => theme.is_published);
  const visibleThemes = catalog.filter((theme) => {
    if (filter === "live" && !theme.is_published) return false;
    if (filter === "draft" && theme.is_published) return false;
    const needle = query.trim().toLowerCase();
    return !needle || `${theme.name} ${theme.tagline} ${theme.description}`.toLowerCase().includes(needle);
  });

  const publishTheme = (theme) => swal({ title: `Publish ${theme.name}?`, text: "Shoppers will see this theme's saved design and homepage.", icon: "warning", buttons: ["Cancel", "Publish"] }).then((ok) => {
    if (!ok) return;
    setBusyKey(theme.theme_key);
    axios.post(`/api/admin/themes/${theme.theme_key}/publish`).then((res) => { swal("Theme live", res.data.message || `${theme.name} is now live.`, "success"); loadThemes(); }).catch((err) => swal("Could not publish", err.response?.data?.message || "Try again.", "error")).finally(() => setBusyKey(""));
  });

  const duplicateTheme = (theme) => swal({ title: `Copy ${theme.name}`, text: "The copy starts with this theme's saved design and homepage.", content: "input", buttons: ["Cancel", "Create theme"] }).then((name) => {
    if (name === null) return;
    setBusyKey(theme.theme_key);
    axios.post(`/api/admin/themes/${theme.theme_key}/duplicate`, { name: String(name || "").trim() || undefined }).then((res) => {
      swal("Theme created", res.data.message || "The new theme is ready.", "success");
      if (res.data.theme?.theme_key) window.location.assign(`/admin/theme-editor?theme=${res.data.theme.theme_key}`); else loadThemes();
    }).catch((err) => swal("Could not copy", err.response?.data?.message || "Try again.", "error")).finally(() => setBusyKey(""));
  });

  const exportTheme = (theme) => {
    setBusyKey(theme.theme_key);
    axios.get(`/api/admin/themes/${theme.theme_key}/package`, { responseType: "blob" }).then((res) => {
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
      const link = document.createElement("a");
      link.href = url; link.download = `${String(theme.name || theme.theme_key).replace(/\s+/g, "-").toLowerCase()}-theme.zip`;
      document.body.appendChild(link); link.click(); link.remove(); URL.revokeObjectURL(url);
    }).catch((err) => swal("Could not export", err.response?.data?.message || "Try again.", "error")).finally(() => setBusyKey(""));
  };

  const importTheme = (event) => {
    const file = event.target.files && event.target.files[0]; event.target.value = ""; if (!file) return;
    const data = new FormData(); data.append("file", file); setImporting(true);
    axios.post("/api/admin/themes/import", data).then((res) => { swal("Theme imported", res.data.message || "The theme was added.", "success"); loadThemes(); }).catch((err) => swal("Could not import", err.response?.data?.message || "Choose a theme package exported from this store.", "error")).finally(() => setImporting(false));
  };

  const deleteTheme = (theme) => swal({ title: `Remove ${theme.name}?`, text: "Products, orders, and the live shop will not be changed.", icon: "warning", buttons: ["Keep it", "Remove"], dangerMode: true }).then((ok) => {
    if (!ok) return;
    setBusyKey(theme.theme_key);
    axios.delete(`/api/admin/themes/${theme.theme_key}`).then(loadThemes).catch((err) => swal("Could not remove", err.response?.data?.message || "Try again.", "error")).finally(() => setBusyKey(""));
  });

  return (
    <div className="theme-library">
      <div className="theme-library-head">
        <div><span className="theme-page-eyebrow">Online store</span><h1>Theme library</h1><p>Preview each storefront, customize its sections, and publish when it is ready.</p></div>
        <div className="theme-library-tools"><button type="button" onClick={() => importRef.current && importRef.current.click()} disabled={importing}><FaUpload /> {importing ? "Importing…" : "Import theme"}</button><input ref={importRef} type="file" accept="application/zip,.zip,application/json,.json" hidden onChange={importTheme} /></div>
      </div>

      {liveTheme && <section className="theme-current">
        <div className="theme-current-preview"><ThemeArtwork theme={liveTheme} large /><button type="button" onClick={() => setDemoTheme(liveTheme)}><span>View showcase</span><FaExternalLinkAlt /></button></div>
        <div className="theme-current-copy"><div><span className="theme-current-kicker">Current theme</span><h2>{liveTheme.name}</h2><p>{liveTheme.tagline}</p></div><span className="theme-live-pill">Live</span><div className="theme-current-actions"><button type="button" className="theme-secondary-button" onClick={() => setDemoTheme(liveTheme)}>View demo</button><Link className="theme-primary-button" to={`/admin/theme-editor?theme=${liveTheme.theme_key}&content=demo`}>Customize</Link></div></div>
      </section>}

      <div className="theme-catalog-head">
        <div><h2>Installed themes</h2><p>{catalog.length} themes available for this store <span className="theme-preview-note">Optimized previews · live demos load on demand</span></p></div>
        <div className="theme-catalog-controls"><div className="theme-filter" role="group" aria-label="Filter themes">{["all", "live", "draft"].map((value) => <button type="button" key={value} className={filter === value ? "is-active" : ""} onClick={() => setFilter(value)}>{value === "all" ? "All" : value === "live" ? "Live" : "Drafts"}</button>)}</div><label className="theme-search"><FaSearch /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search themes" /></label></div>
      </div>

      <div className="theme-library-grid">{visibleThemes.map((theme) => <article key={theme.theme_key} className={`theme-card ${theme.is_published ? "is-live" : ""}`}>
        <button type="button" className="theme-card-preview" onClick={() => setDemoTheme(theme)} aria-label={`View ${theme.name} demo`}><ThemeArtwork theme={theme} /><span className="theme-card-preview-action"><FaDesktop /> View demo</span></button>
        <div className="theme-card-body">
          <div className="theme-card-title"><div><h3>{theme.name}</h3><p>{theme.tagline}</p></div>{theme.is_published ? <span className="theme-live-pill">Live</span> : <span className="theme-draft-pill">Draft</span>}</div>
          <div className="theme-feature-list">{(theme.features || []).slice(0, 3).map((feature) => <span key={feature}>{feature}</span>)}</div>
          <div className="theme-card-actions"><button type="button" className="theme-secondary-button" onClick={() => setDemoTheme(theme)}>View demo</button><Link className="theme-primary-button" to={`/admin/theme-editor?theme=${theme.theme_key}&content=demo`}>Customize</Link><details className="theme-card-menu"><summary aria-label={`More actions for ${theme.name}`}><FaEllipsisH /></summary><div>
            {!theme.is_published && <button type="button" onClick={() => publishTheme(theme)} disabled={busyKey === theme.theme_key}>Publish theme</button>}
            <button type="button" onClick={() => duplicateTheme(theme)} disabled={busyKey === theme.theme_key}><FaRegCopy /> Duplicate</button><button type="button" onClick={() => exportTheme(theme)} disabled={busyKey === theme.theme_key}><FaDownload /> Export package</button>
            {canRemoveTheme(theme) && <button type="button" className="is-danger" onClick={() => deleteTheme(theme)} disabled={busyKey === theme.theme_key}>Remove theme</button>}
          </div></details></div>
        </div>
      </article>)}</div>
      {!loading && !visibleThemes.length && <div className="theme-library-empty"><h3>No matching themes</h3><p>Try another search or filter.</p></div>}
      {loading && <p className="theme-library-status">Loading saved themes…</p>}
      {demoTheme && <DemoModal theme={demoTheme} onClose={() => setDemoTheme(null)} />}
    </div>
  );
}
